import { EnumTypeTexts } from "../Common/Enums/EnumsTypes";
import {
  InitialStateProps,
  ActionKind,
  ReducerAction,
} from "./SupplierViewPropsTypes";
import produce from "immer";
import { NotificationManager } from "react-notifications";
import { Notify } from "../Common/Notify/NotificationMessages";
import { validateFile } from "../Common/CommonFunctions/validateFile";

export const INITIAL_STATE: InitialStateProps = {
  apiLoading: false,
  modalInfo: {
    noteModalOpen: false,
    attachmentModalOpen: false,
  },
  modalNoteAndAttQuestionId: "",
  uploadedFiles: new Map(),
  allQuestionIds: [],
  questionIdsToRender: [],
  noteState: {},
  fileUploadModal: false,
  fileUploadQuestionId: "",
  maxFileSizeExceededNames: new Map(),
  numberQuesHash: new Set(),
  validNumberErrorMessage: {},
};
export const reducer = (
  state: InitialStateProps,
  action: ReducerAction
): InitialStateProps => {
  switch (action.type) {
    case ActionKind.API_LOADING:
      return {
        ...state,
        apiLoading: action?.payload,
      };

    case ActionKind.HANDLE_NOTE_AND_ATTACH_MODAL:
      if (action.payload?.modalType === EnumTypeTexts?.NOTE) {
        return {
          ...state,
          modalInfo: {
            ...state.modalInfo,
            noteModalOpen: !state.modalInfo.noteModalOpen,
          },
          ...(action?.payload?.questionId && {
            modalNoteAndAttQuestionId: action?.payload?.questionId,
          }),
          noteState: {} as InitialStateProps["noteState"],
        };
      } else {
        return {
          ...state,
          modalInfo: {
            ...state.modalInfo,
            attachmentModalOpen: !state.modalInfo.attachmentModalOpen,
          },
          ...(action?.payload?.questionId && {
            modalNoteAndAttQuestionId: action?.payload?.questionId,
          }),
        };
      }
    case ActionKind.OPEN_FILE_UPLOAD_MODAL:
      return {
        ...state,
        fileUploadModal: action.payload.toggleState,
        fileUploadQuestionId: action.payload.questionId!,
        maxFileSizeExceededNames: new Map(),
      };
    case ActionKind.CLEAR_UPLOADED_FILES:
      return produce(state, (draft) => {
        draft.uploadedFiles.delete(action?.payload?.questionId);
      });
    case ActionKind.HANDLE_FILE_CHANGE:
      const fileResult = validateFile(action.payload.files);
      if (!fileResult.isValid) {
        NotificationManager.warning(fileResult.message);
      } else {
        let fileArrNew = Object.values(action.payload.files);

        let total: any = fileArrNew.reduce(
          (acc: any, curr: any) => acc + curr.size,
          0
        );
        const fileSize = {
          trackerFileSize: 2097152,
        };
        if (total <= fileSize.trackerFileSize) {
          const newState = produce(state, (draft) => {
            if (draft.uploadedFiles.has(draft.modalNoteAndAttQuestionId)) {
              draft.uploadedFiles
                .get(draft.modalNoteAndAttQuestionId)
                ?.selectedFiles.push(...action.payload.files);
            } else {
              draft.uploadedFiles.set(draft.modalNoteAndAttQuestionId, {
                selectedFiles: [...action.payload.files],
              });
            }
          });

          return { ...state, uploadedFiles: newState.uploadedFiles };
        } else {
          NotificationManager.warning(Notify?.FILE_SIZE_WARN, "", 2000);
        }
      }
      return { ...state };
    // for file upload question
    case ActionKind.HANDLE_FILE_CHANGE_FOR_FILE_UPLOAD:
      let fileArr: any = Object.values(action.payload.files);
      const fileResultUpload = validateFile(action.payload.files);
      if (!fileResultUpload.isValid) {
        NotificationManager.warning(fileResultUpload.message);
      } else {
        if (
          Number(
            state.uploadedFiles.get(state.fileUploadQuestionId)?.selectedFiles
              .length! ?? 0
          ) +
            fileArr.length >
          action.payload.fileLimit
        ) {
          setTimeout(() => {
            NotificationManager.warning(
              Notify?.FILE_LIMIT(action.payload.fileLimit),
              "",
              2000
            );
          }, 0);
        } else {
          return produce(state, (draft) => {
            draft.maxFileSizeExceededNames = new Map();
            for (let element of fileArr) {
              if (element.size <= action.payload?.maxFileSize! * 1000000) {
                if (draft.uploadedFiles.has(state.fileUploadQuestionId)) {
                  draft.uploadedFiles
                    .get(state.fileUploadQuestionId)
                    ?.selectedFiles.push(element);
                } else {
                  draft.uploadedFiles.set(state.fileUploadQuestionId, {
                    selectedFiles: [element],
                  });
                }
              } else {
                if (
                  draft.maxFileSizeExceededNames.has(state.fileUploadQuestionId)
                ) {
                  draft.maxFileSizeExceededNames
                    .get(state.fileUploadQuestionId)!
                    .add(element.name);
                } else {
                  draft.maxFileSizeExceededNames.set(
                    state.fileUploadQuestionId,
                    new Set([element.name])
                  );
                }
              }
            }
          });
        }
      }
      return state;

    case ActionKind.CREATE_NEW_UPLOADED_FILE_MAP:
      return produce(state, (draft) => {
        draft.uploadedFiles = new Map();
      });

    case ActionKind.ALL_QUESTION_IDS:
      return {
        ...state,
        allQuestionIds: action.payload.allQuestionIdsDummy,
      };
    case ActionKind.QUESTION_IDS_TO_RENDER:
      return {
        ...state,
        questionIdsToRender: action.payload.updatedFilterData,
      };
    case ActionKind.REMOVE_SELECTED_FILES:
      let FilteredAttachedFiles: any = [];
      return produce(state, (draft) => {
        FilteredAttachedFiles = draft?.uploadedFiles
          .get(draft.modalNoteAndAttQuestionId)
          ?.selectedFiles.filter((data: any) => data.name !== action.payload);
        draft.uploadedFiles.get(
          draft.modalNoteAndAttQuestionId
        )!.selectedFiles = FilteredAttachedFiles;
      });
    case ActionKind.REMOVE_ADDED_FILES_FOR_FILE_UPLOAD:
      let FilteredAttachedFilesForFileUpload: any = [];
      return produce(state, (draft) => {
        FilteredAttachedFilesForFileUpload = draft?.uploadedFiles
          .get(draft.fileUploadQuestionId)
          ?.selectedFiles.filter((data: any) => data.name !== action.payload);
        draft.uploadedFiles.get(draft.fileUploadQuestionId)!.selectedFiles =
          FilteredAttachedFilesForFileUpload;
      });

    case ActionKind.NOTE_ONCHANGE:
      const newNoteState = {
        ...state.noteState,
        [state.modalNoteAndAttQuestionId]: action.payload,
      };
      return {
        ...state,
        noteState: newNoteState,
      };
    case ActionKind.SET_NOTE_STATE_FROM_SURVEY_ANSWER:
      const nonMutableNoteState = { ...state.noteState };
      if (action.payload[state.modalNoteAndAttQuestionId]) {
        nonMutableNoteState[state.modalNoteAndAttQuestionId] =
          action.payload[state.modalNoteAndAttQuestionId].comment || "";
      }
      return { ...state, noteState: nonMutableNoteState };
    case ActionKind.CLEAR_NOTE:
      const updatedNoteState = { ...state.noteState };
      delete updatedNoteState[action.payload];
      return {
        ...state,
        noteState: updatedNoteState,
      };
    case ActionKind.SET_NUMBER_QUES_ID_HASH:
      return {
        ...state,
        numberQuesHash: new Set([...state.numberQuesHash, action.payload]),
      };
    case ActionKind.REMOVE_QUES_ID_FROM_NUM_HASH:
      const updatedSet = new Set(state.numberQuesHash);
      updatedSet.delete(action.payload);
      return {
        ...state,
        numberQuesHash: updatedSet,
      };

    case ActionKind.SET_VALID_NUMBER_ERROR_MSG:
      if (!action.payload.errorMessage) {
        const newState = { ...state.validNumberErrorMessage };
        delete newState[action.payload.questionId];
        return {
          ...state,
          validNumberErrorMessage: newState,
        };
      } else {
        return {
          ...state,
          validNumberErrorMessage: {
            ...state.validNumberErrorMessage,
            [action.payload.questionId]: action.payload.errorMessage,
          },
        };
      }
    default:
      return state;
  }
};
